import { z } from 'zod'

/** `starts_at` / `ends_at` are gym-local calendar days, stored as `date`. */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const dayString = z.string().regex(DATE_RE, 'Expected a YYYY-MM-DD date')

export const trainerAssignmentSchema = z
  .object({
    trainer_user_id: z.string().min(1, 'Pick a trainer'),
    member_user_id: z.string().min(1, 'Pick a member'),
    /** null or absent means the adult member; a uuid means one of their children. */
    child_id: z.string().uuid().optional().nullable(),
    starts_at: dayString.optional().nullable(),
    /** Absent means open-ended: the assignment runs until someone ends it. */
    ends_at: dayString.optional().nullable(),
    note: z.string().max(500).optional().nullable(),
  })
  .refine((v) => !v.starts_at || !v.ends_at || v.ends_at >= v.starts_at, {
    message: 'The end date cannot be before the start date',
    path: ['ends_at'],
  })

export type TrainerAssignmentInput = z.infer<typeof trainerAssignmentSchema>

/**
 * Editing a live assignment — in practice pushing `ends_at` out when the
 * trainer is away and the member's block has to run longer.
 *
 * `ends_at: null` is meaningful and distinct from omitting it: null clears the
 * end date and makes the assignment open-ended, omitting it leaves it alone.
 */
export const trainerAssignmentUpdateSchema = z
  .object({
    ends_at: dayString.nullable().optional(),
    note: z.string().max(500).nullable().optional(),
  })
  .refine((v) => v.ends_at !== undefined || v.note !== undefined, {
    message: 'Nothing to update',
  })

export type TrainerAssignmentUpdateInput = z.infer<typeof trainerAssignmentUpdateSchema>

/**
 * Handing a member to a different trainer mid-block. The remaining period is
 * carried over unless a new end date is given, and `starts_at` defaults to
 * today — the day the new trainer actually takes over.
 */
export const trainerReassignSchema = z.object({
  trainer_user_id: z.string().min(1, 'Pick a trainer'),
  starts_at: dayString.optional().nullable(),
  ends_at: dayString.optional().nullable(),
  note: z.string().max(500).optional().nullable(),
})

export type TrainerReassignInput = z.infer<typeof trainerReassignSchema>

/**
 * A trainer being away — holiday, illness — applied across their whole
 * caseload at once.
 *
 * `extend` pushes every dated assignment out by the length of the absence, for
 * when they are coming back. `reassign` hands the caseload to somebody else,
 * for when they are not; it needs `cover_user_id`.
 */
export const trainerUnavailabilitySchema = z
  .object({
    from: dayString,
    to: dayString,
    mode: z.enum(['extend', 'reassign']),
    cover_user_id: z.string().min(1).optional().nullable(),
  })
  .refine((v) => v.to >= v.from, {
    message: 'The last day away cannot be before the first',
    path: ['to'],
  })
  .refine((v) => v.mode !== 'reassign' || !!v.cover_user_id, {
    message: 'Pick who covers for them',
    path: ['cover_user_id'],
  })

export type TrainerUnavailabilityInput = z.infer<typeof trainerUnavailabilitySchema>
